import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  CarProfile,
  User as UserIcon,
  ArrowRight,
  ShieldCheck,
  UploadSimple,
  IdentificationCard,
  FileText,
} from "@phosphor-icons/react";

const ACCEPT_DOC = "image/jpeg,image/png,image/webp,image/gif,application/pdf";

function FileChip({ file, onClear, testId }) {
  if (!file) return null;
  return (
    <div className="flex items-center justify-between gap-2 mt-2 border-2 border-foreground p-2 bg-background text-sm" data-testid={testId}>
      <div className="flex items-center gap-2 truncate">
        <FileText size={16} weight="duotone" />
        <span className="truncate">{file.name}</span>
        <span className="font-mono text-[10px] text-foreground/60">{Math.round(file.size / 1024)} KB</span>
      </div>
      <button onClick={onClear} className="font-mono text-[10px] uppercase tracking-wider border border-foreground px-2 py-0.5 hover:bg-secondary">
        Clear
      </button>
    </div>
  );
}

export default function Onboarding() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [role, setRole] = useState(null);
  const [name, setName] = useState(user?.name || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [address, setAddress] = useState(user?.address || "");
  const [idFile, setIdFile] = useState(null);
  const [licenseFile, setLicenseFile] = useState(null);
  const [veh, setVeh] = useState({ vehicle_make: "", vehicle_model: "", vehicle_color: "", vehicle_plate: "" });
  const [busy, setBusy] = useState(false);

  React.useEffect(() => {
    if (user?.role === "admin") {
      navigate("/admin", { replace: true });
    } else if (user?.role === "rider" || user?.role === "driver") {
      navigate(`/${user.role}`, { replace: true });
    }
  }, [user, navigate]);

  if (!user) return null;

  const phoneOk = /^[+\d][\d\s\-()]{6,20}$/.test(phone.trim());
  const nameOk = name.trim().length >= 2;
  const addressOk = role !== "driver" || address.trim().length >= 4;
  const vehicleOk = role !== "driver" || (veh.vehicle_make && veh.vehicle_model && veh.vehicle_color && veh.vehicle_plate);
  const licenseOk = role !== "driver" || !!licenseFile;
  const canSubmit = role && nameOk && phoneOk && addressOk && vehicleOk && licenseOk;

  const validateFile = (f) => {
    if (!f) return false;
    if (f.size > 5 * 1024 * 1024) {
      toast.error("File too large (max 5 MB)");
      return false;
    }
    return true;
  };

  const submit = async () => {
    if (!canSubmit) {
      toast.error("Please complete all required fields");
      return;
    }
    setBusy(true);
    try {
      // 1. Set role + required profile fields
      await api.post("/users/role", {
        role,
        name: name.trim(),
        phone: phone.trim(),
        address: role === "driver" ? address.trim() : undefined,
      });

      // 2. Upload documents
      if (role === "driver") {
        const fd = new FormData();
        fd.append("file", licenseFile);
        await api.post("/drivers/upload-license", fd, { headers: { "Content-Type": "multipart/form-data" } });
        await api.post("/drivers/register", veh);
      }
      if (idFile) {
        const fd = new FormData();
        fd.append("file", idFile);
        await api.post("/users/upload-id", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }

      await refresh();
      toast.success("You're all set");
      let stashed = null;
      try { stashed = sessionStorage.getItem("ridegy:post_signin_redirect"); } catch { /* private mode */ }
      if (stashed) {
        try { sessionStorage.removeItem("ridegy:post_signin_redirect"); } catch { /* private mode */ }
        navigate(stashed);
      } else {
        navigate(`/${role}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <div className="font-mono text-xs uppercase tracking-[0.3em] text-foreground/60 mb-3">
          Welcome, {user.name?.split(" ")[0] || "friend"}
        </div>
        <h1 className="font-display text-4xl lg:text-5xl tracking-tighter mb-2">
          Set up your RideGY account
        </h1>
        <p className="text-foreground/70 mb-10">Tell us a bit about yourself — name and phone are required.</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border-2 border-foreground">
          {[
            { id: "rider", title: "I'm a Passenger", desc: "Book cash-only rides across Guyana.", icon: UserIcon },
            { id: "driver", title: "I'm a Driver", desc: "Earn cash driving on your schedule.", icon: CarProfile },
          ].map((opt, i) => (
            <button
              key={opt.id}
              data-testid={`onboarding-role-${opt.id}`}
              onClick={() => setRole(opt.id)}
              className={`text-left p-8 transition-colors ${i === 0 ? "border-b-2 md:border-b-0 md:border-r-2 border-foreground" : ""} ${
                role === opt.id ? "bg-primary text-foreground" : "bg-card hover:bg-secondary"
              }`}
            >
              <opt.icon size={36} weight="duotone" />
              <div className="font-display text-2xl mt-4">{opt.title}</div>
              <div className="text-foreground/70 mt-1">{opt.desc}</div>
            </button>
          ))}
        </div>

        {role && (
          <div className="mt-8 space-y-6">
            {/* Personal info */}
            <div className="border-2 border-foreground p-6 bg-card">
              <h2 className="font-display text-2xl mb-4">Your details</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="font-mono text-xs uppercase tracking-wider">
                    Full name <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    data-testid="onboarding-name-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="John Persaud"
                    className="rounded-none mt-1 border-2 border-foreground/30 h-11"
                  />
                </div>
                <div>
                  <Label className="font-mono text-xs uppercase tracking-wider">
                    Phone <span className="text-destructive">*</span>
                    <span className="ml-2 inline-flex items-center gap-1 text-accent normal-case font-normal text-[10px]">
                      Used for WhatsApp & calls
                    </span>
                  </Label>
                  <Input
                    data-testid="onboarding-phone-input"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+592 612 3456"
                    className="rounded-none mt-1 border-2 border-foreground/30 h-11"
                  />
                  {!phoneOk && phone.length > 0 && (
                    <div className="text-xs text-destructive mt-1">Enter a valid phone number with country code (e.g. +592...)</div>
                  )}
                </div>
                {role === "driver" && (
                  <div className="md:col-span-2">
                    <Label className="font-mono text-xs uppercase tracking-wider">
                      Home address <span className="text-destructive">*</span>
                    </Label>
                    <Textarea
                      data-testid="onboarding-address-input"
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      placeholder="Lot 22, Lamaha Street, Georgetown"
                      className="rounded-none mt-1 border-2 border-foreground/30"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Rider: optional ID for verified badge */}
            {role === "rider" && (
              <div className="border-2 border-foreground p-6 bg-card">
                <div className="flex items-center justify-between mb-2">
                  <h2 className="font-display text-2xl">Verify your identity</h2>
                  <span className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider border-2 border-foreground px-2 py-1">
                    Optional
                  </span>
                </div>
                <p className="text-foreground/70 text-sm mb-3">
                  Upload a photo of your national ID or passport. Verified riders get a <ShieldCheck size={14} weight="fill" className="inline text-accent" /> badge and priority pickup matching.
                </p>
                <label className="block">
                  <input
                    data-testid="onboarding-id-file"
                    type="file"
                    accept={ACCEPT_DOC}
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f && validateFile(f)) setIdFile(f);
                    }}
                  />
                  <div className="cursor-pointer border-2 border-dashed border-foreground/40 hover:border-foreground p-6 flex items-center gap-3 text-foreground/70 hover:text-foreground transition-colors">
                    <IdentificationCard size={28} weight="duotone" />
                    <div>
                      <div className="font-semibold">Upload ID / passport</div>
                      <div className="text-xs">JPG · PNG · PDF · up to 5 MB</div>
                    </div>
                    <UploadSimple size={20} className="ml-auto" />
                  </div>
                </label>
                <FileChip file={idFile} onClear={() => setIdFile(null)} testId="onboarding-id-file-chip" />
              </div>
            )}

            {/* Driver: required license + vehicle */}
            {role === "driver" && (
              <>
                <div className="border-2 border-foreground p-6 bg-card">
                  <div className="flex items-center justify-between mb-2">
                    <h2 className="font-display text-2xl">Driver license</h2>
                    <span className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider border-2 border-destructive text-destructive px-2 py-1">
                      Required
                    </span>
                  </div>
                  <p className="text-foreground/70 text-sm mb-3">
                    Upload a clear photo or PDF of your valid driver's license. You won't be able to go online until this is provided.
                  </p>
                  <label className="block">
                    <input
                      data-testid="onboarding-license-file"
                      type="file"
                      accept={ACCEPT_DOC}
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f && validateFile(f)) setLicenseFile(f);
                      }}
                    />
                    <div className={`cursor-pointer border-2 border-dashed p-6 flex items-center gap-3 transition-colors ${licenseFile ? "border-accent text-foreground" : "border-foreground/40 hover:border-foreground text-foreground/70 hover:text-foreground"}`}>
                      <IdentificationCard size={28} weight="duotone" />
                      <div>
                        <div className="font-semibold">Upload driver license</div>
                        <div className="text-xs">JPG · PNG · PDF · up to 5 MB</div>
                      </div>
                      <UploadSimple size={20} className="ml-auto" />
                    </div>
                  </label>
                  <FileChip file={licenseFile} onClear={() => setLicenseFile(null)} testId="onboarding-license-file-chip" />
                  {!licenseOk && (
                    <div className="text-xs text-destructive mt-2">Driver license is required to continue</div>
                  )}
                </div>

                <div className="border-2 border-foreground p-6 bg-card">
                  <h2 className="font-display text-2xl mb-4">Vehicle details</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="font-mono text-xs uppercase tracking-wider">Make <span className="text-destructive">*</span></Label>
                      <Input
                        data-testid="onboarding-veh-make"
                        value={veh.vehicle_make}
                        onChange={(e) => setVeh({ ...veh, vehicle_make: e.target.value })}
                        placeholder="Toyota"
                        className="rounded-none mt-1 border-2 border-foreground/30 h-11"
                      />
                    </div>
                    <div>
                      <Label className="font-mono text-xs uppercase tracking-wider">Model <span className="text-destructive">*</span></Label>
                      <Input
                        data-testid="onboarding-veh-model"
                        value={veh.vehicle_model}
                        onChange={(e) => setVeh({ ...veh, vehicle_model: e.target.value })}
                        placeholder="Allion"
                        className="rounded-none mt-1 border-2 border-foreground/30 h-11"
                      />
                    </div>
                    <div>
                      <Label className="font-mono text-xs uppercase tracking-wider">Color <span className="text-destructive">*</span></Label>
                      <Input
                        data-testid="onboarding-veh-color"
                        value={veh.vehicle_color}
                        onChange={(e) => setVeh({ ...veh, vehicle_color: e.target.value })}
                        placeholder="Silver"
                        className="rounded-none mt-1 border-2 border-foreground/30 h-11"
                      />
                    </div>
                    <div>
                      <Label className="font-mono text-xs uppercase tracking-wider">Plate <span className="text-destructive">*</span></Label>
                      <Input
                        data-testid="onboarding-veh-plate"
                        value={veh.vehicle_plate}
                        onChange={(e) => setVeh({ ...veh, vehicle_plate: e.target.value })}
                        placeholder="PRR 1234"
                        className="rounded-none mt-1 border-2 border-foreground/30 h-11"
                      />
                    </div>
                  </div>
                </div>
              </>
            )}

            <div>
              <Button
                data-testid="onboarding-submit-btn"
                onClick={submit}
                disabled={busy || !canSubmit}
                className="rounded-none h-12 px-6 bg-foreground text-background hover:bg-foreground/90 disabled:opacity-50"
              >
                {busy ? "Saving…" : "Continue"} <ArrowRight size={18} />
              </Button>
              {!canSubmit && (
                <span className="ml-3 font-mono text-[10px] uppercase tracking-wider text-foreground/60">
                  Complete required (*) fields
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
